// Types
import { TDrawDepartment, TDrawRecord, TMergedDraw, TNewDrawRow } from "../types";
import { GridRowHeightParams, GridRowHeightReturnValue, GridValidRowModel } from "@mui/x-data-grid-premium";

// Constants
import { DEFAULT_HEIGHT, DRAW_ID_MAP } from "../constants";
import { IDrawInfo, IOverdrawDetails } from "@/DataPages/types/draw/models";
import { getPositionDepartment } from "@/Common/helpers/Positions";

function calculateTotalDrawAmount(draws: Array<{ amount: number }>) {
	if (!draws?.length) {
		return 0;
	}

	return draws.reduce((total, draw) => total + (draw.amount || 0), 0);
}
function transformNewRows(data: Array<TDrawRecord>) {
	const newRows: Array<TNewDrawRow> = [];

	data.forEach((item) => {
		const totalDrawAmount = calculateTotalDrawAmount(item.draws);
		newRows.push({
			_id: item._id,
			hierarchy: [item._id],
			fullName: item.fullName,
			searchName: item.fullName,
			drawAmount: totalDrawAmount,
			overdraw: item.overdraw,
		});

		if (totalDrawAmount === 0) {
			return;
		}

		newRows.push({
			_id: `${DRAW_ID_MAP.DRAW_TEXT}_${item._id}`,
			hierarchy: [item._id, `${DRAW_ID_MAP.DRAW_TEXT}_${item._id}`],
			fullName: "Draw Type",
			searchName: item.fullName,
			drawAmount: "",
			overdraw: "",
		});

		item.draws.forEach((draw) => {
			const _newId = `${DRAW_ID_MAP.CHILD}_${item._id}_${draw.drawType.split(" ").join("_")}`;
			newRows.push({
				_id: _newId,
				hierarchy: [item._id, _newId],
				fullName: draw.drawType,
				searchName: item.fullName,
				drawAmount: draw.amount,
				overdraw: "",
			});
		});

		newRows.push({
			_id: `${DRAW_ID_MAP.LAST_DRAW}_${item._id}`,
			hierarchy: [item._id, `${DRAW_ID_MAP.LAST_DRAW}_${item._id}`],
			fullName: "",
			searchName: item.fullName,
			drawAmount: "",
			overdraw: "",
		});
	});

	return newRows;
}

function getTreeDataPath(row: GridValidRowModel) {
	return row?.hierarchy ?? [];
}

function isItOverDraw(id: string) {
	return isItDrawText(id) || isItLastDraw(id);
}

function isItChild(id: string) {
	return id.includes(`${DRAW_ID_MAP.CHILD}_`);
}

function isItDrawText(id: string) {
	return id.includes(`${DRAW_ID_MAP.DRAW_TEXT}_`);
}

function isItLastDraw(id: string) {
	return id.includes(`${DRAW_ID_MAP.LAST_DRAW}_`);
}

function isItAnyDraw(id: string) {
	return isItChild(id) || isItDrawText(id) || isItLastDraw(id);
}

function determineRowHeight({ id }: GridRowHeightParams): GridRowHeightReturnValue {
	if (typeof id !== "string") {
		return DEFAULT_HEIGHT;
	}

	if (isItChild(id)) {
		return DEFAULT_HEIGHT;
	}

	if (isItDrawText(id)) {
		return 50;
	}

	if (isItLastDraw(id)) {
		return DEFAULT_HEIGHT;
	}
}

function matchDepartments(positions: string[] = [], draws: IDrawInfo[] = [], overdraws: IOverdrawDetails[] = []) {
	const departments = new Map<string, TDrawDepartment>();
	const filteredDraws: IDrawInfo[] = [];
	const filteredOverdraws: IOverdrawDetails[] = [];

	draws.forEach((record) => {
		if (record?.user && positions.includes(record?.user?.position ?? "")) {
			filteredDraws.push(record);
		}
	});

	overdraws.forEach((record) => {
		if (record?.employee && positions.includes(record?.employee?.position ?? "")) {
			filteredOverdraws.push(record);
		}
	});

	if (filteredDraws.length || filteredOverdraws.length) {
		const information = mergeRecords(filteredDraws, filteredOverdraws);

		positions.forEach((position) => {
			const department = getPositionDepartment(position);
			const stored = departments.get(department) || { department, listPositions: [] };
			const records = getPositionRecords(position, information);
			if (records?.length) {
				stored.listPositions.push({
					position,
					records,
				});

				departments.set(department, stored);
			}
		});
	}

	return Array.from(departments.values());
}

function mergeRecords(drawRecords: IDrawInfo[] = [], overdrawRecords: IOverdrawDetails[] = []) {
	const recordsMap = new Map<string, TMergedDraw>();

	drawRecords.forEach((element: IDrawInfo) => {
		const key = element?.user?._id;

		if (key) {
			const record = recordsMap.get(key) || {
				_id: key,
				fullName: element?.user?.fullName ?? "",
				position: element?.user?.position ?? "",
				draws: element?.drawItems ?? [],
				overdraw: 0,
			};

			recordsMap.set(key, record);
		}
	});

	overdrawRecords.forEach((element) => {
		const record = recordsMap.get(element.employee._id) || {
			_id: element.employee._id,
			fullName: element.employee.name,
			position: element.employee.position,
			draws: [],
			overdraw: 0,
		};

		const amount = element.overdraw || 0.0;
		record.overdraw = Number(amount.toFixed(2));
		recordsMap.set(element.employee._id, record);
	});

	return Array.from(recordsMap.values());
}

function getPositionRecords(position: string, records: TMergedDraw[] = []) {
	const results = records.filter((record) => record.position === position);

	return results.map(({ position, ...rest }) => rest);
}

export {
	calculateTotalDrawAmount,
	transformNewRows,
	getTreeDataPath,
	isItOverDraw,
	isItChild,
	isItLastDraw,
	isItDrawText,
	isItAnyDraw,
	determineRowHeight,
	matchDepartments,
};
